/* ==========================================================================
   Changelog Entry Component
   Renders a single version entry in the changelog
   ========================================================================== */

import type { ReactNode } from 'react'
import { Card } from '@/components/ui/Card'
import type { ChangelogVersion } from '@/hooks/useChangelog'

/* --------------------------------------------------------------------------
   Inline markup
   -------------------------------------------------------------------------- */

/** Matches a `**bold**` or `` `code` `` span, capturing the text inside. */
const INLINE_MARKUP = /\*\*([\s\S]+?)\*\*|`([^`]+?)`/g

/**
 * Renders the small subset of Markdown the changelog entries are authored in.
 *
 * Highlights in `changelog.json` have always been written with `**bold**` (and
 * `` `code` `` in one entry), but reached the page as a plain string — so
 * readers saw literal asterisks and backticks.
 *
 * Returns React nodes rather than an HTML string, so entry text is escaped as
 * usual and can never be interpreted as markup. Unpaired delimiters simply
 * don't match and render as written.
 */
function renderInlineMarkup(text: string): ReactNode[] {
  const nodes: ReactNode[] = []
  let cursor = 0

  for (const match of text.matchAll(INLINE_MARKUP)) {
    const [raw, bold, code] = match
    const start = match.index ?? 0

    if (start > cursor) nodes.push(text.slice(cursor, start))
    nodes.push(
      bold !== undefined ? (
        <strong key={start} className="font-semibold">
          {bold}
        </strong>
      ) : (
        <code
          key={start}
          className="font-mono text-[0.9em] bg-gray-100 rounded px-1 py-0.5"
        >
          {code}
        </code>
      ),
    )
    cursor = start + raw.length
  }

  if (cursor < text.length) nodes.push(text.slice(cursor))
  return nodes
}

/* --------------------------------------------------------------------------
   Tag Badge
   -------------------------------------------------------------------------- */

const TAG_STYLES: Record<string, string> = {
  feature: 'bg-[var(--color-soft-lavender)] text-[var(--color-deep-purple)]',
  improvement: 'bg-blue-100 text-blue-700',
  fix: 'bg-amber-100 text-amber-700',
}

const TAG_LABELS: Record<string, string> = {
  feature: 'New Feature',
  improvement: 'Improvement',
  fix: 'Bug Fix',
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

interface ChangelogEntryProps {
  entry: ChangelogVersion
}

export function ChangelogEntry({ entry }: ChangelogEntryProps) {
  const formattedDate = new Date(entry.date + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  return (
    <Card>
      {/* Header: version, tag, date */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-xs font-mono font-medium text-[var(--color-charcoal)] bg-gray-100 rounded px-2 py-0.5">
          v{entry.version}
        </span>
        <span
          className={`text-xs font-medium rounded-full px-2.5 py-0.5 ${TAG_STYLES[entry.tag] ?? TAG_STYLES.feature}`}
        >
          {TAG_LABELS[entry.tag] ?? entry.tag}
        </span>
        <span className="text-xs text-gray-500 ml-auto">
          {formattedDate}
        </span>
      </div>

      {/* Title */}
      <h3 className="font-serif text-lg text-[var(--color-charcoal)] mb-3">
        {entry.title}
      </h3>

      {/* Provider highlights */}
      {entry.providerHighlights.length > 0 && (
        <div className="mb-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-charcoal)]/50 mb-1.5">
            For Providers
          </h4>
          <ul className="space-y-1.5">
            {entry.providerHighlights.map((highlight, i) => (
              <li key={i} className="flex gap-2 text-sm text-[var(--color-charcoal)]">
                <span className="text-[var(--color-deep-purple)] mt-1 flex-shrink-0">&#8226;</span>
                <span>{renderInlineMarkup(highlight)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Admin highlights */}
      {entry.adminHighlights.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-charcoal)]/50 mb-1.5">
            For Pharmacy Admins
          </h4>
          <ul className="space-y-1.5">
            {entry.adminHighlights.map((highlight, i) => (
              <li key={i} className="flex gap-2 text-sm text-[var(--color-charcoal)]">
                <span className="text-[var(--color-deep-purple)] mt-1 flex-shrink-0">&#8226;</span>
                <span>{renderInlineMarkup(highlight)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Optional details */}
      {entry.details && (
        <p className="mt-3 text-sm text-[var(--color-charcoal)]/70 leading-relaxed">
          {renderInlineMarkup(entry.details)}
        </p>
      )}
    </Card>
  )
}
